import axios, { AxiosInstance } from 'axios';
import securityConfig from '../config/security';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.e-point.app';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: securityConfig.REQUEST_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(securityConfig.TOKEN_STORAGE_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - but don't redirect if we're on auth page
          const currentPath = window.location.pathname;
          if (currentPath !== '/auth' && currentPath !== '/login') {
            localStorage.removeItem(securityConfig.TOKEN_STORAGE_KEY);
            localStorage.removeItem(securityConfig.REFRESH_TOKEN_KEY);
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic methods
  async get<T>(endpoint: string, params?: any): Promise<T> {
    const response = await this.api.get(endpoint, { params });
    // Backend has TransformInterceptor that wraps response in { data: actualData, statusCode, ... }
    // So we need to access response.data.data for the actual payload
    return response.data.data !== undefined ? response.data.data : response.data;
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.post(endpoint, data);
    return response.data.data !== undefined ? response.data.data : response.data;
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.put(endpoint, data);
    return response.data.data !== undefined ? response.data.data : response.data;
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.patch(endpoint, data);
    return response.data.data !== undefined ? response.data.data : response.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.api.delete(endpoint);
    return response.data.data !== undefined ? response.data.data : response.data;
  }

  // Get the axios instance for direct use if needed
  getAxiosInstance(): AxiosInstance {
    return this.api;
  }

  // Get the base URL for constructing download URLs
  getBaseUrl(): string {
    return API_BASE_URL;
  }
}

export const apiService = new ApiService();
export default apiService;